import { ENV, MAX_CHARACTERS } from "../enviormentVariables";

/**
 *
 * @returns true if the client is using a mobile device
 */
export function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function fallbackCopy(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.height = "0px";
  textarea.style.width = "0px";
  textarea.style.position = "absolute";
  // textarea.style.visibility = "hidden";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    const success = document.execCommand("copy");
    Log("Fallback copy success:", success);
  } catch (err) {
    console.error("Fallback copy failed:", err);
  }
  document.body.removeChild(textarea);
}

export function tryParse(text) {
  try {
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
}

export function MbToBytes(megabytes) {
  return megabytes * 1048576;
}

export function BytesToMb(bytes) {
  return bytes / 1048576;
}

export function BytesToGb(bytes) {
  return bytes / 1074000000; // 1 GB = 2^30 bytes = 1073741824 bytes
}

export function GbToBytes(gigabytes) {
  return gigabytes * 1073741824; // 1 GB = 1073741824 bytes
}

/**
 * Tracks a url in session storage, to optimize file retrieval
 * @param {*} objectId
 * @param {*} url
 */
export function TrackSessionUrl(objectId, url) {
  let urls = sessionStorage.getItem("object_urls");
  if (urls) {
    urls = tryParse(urls);
    urls.push({ objectId, url }); // urls should be an array
  } else {
    urls = [{ objectId, url }];
  }
  sessionStorage.setItem("object_urls", JSON.stringify(urls));
}

/**
 * Removes a specific URL from session storage by objectId
 * @param {*} objectId
 */
export function RemoveSessionUrl(objectId) {
  let urls = sessionStorage.getItem("object_urls");
  if (!urls) return;

  urls = tryParse(urls);
  if (!Array.isArray(urls)) return;

  const filtered = urls.filter((entry) => entry.objectId !== objectId);
  sessionStorage.setItem("object_urls", JSON.stringify(filtered));
}

/**
 * Gets a list of session urls currently stored in session storage
 * @returns {[{objectId, url}]}
 */
export function GetSessionUrls() {
  let urls = tryParse(sessionStorage.getItem("object_urls"));
  return urls ?? [];
}

/**
 * Revoke all session urls from session storage
 */
export function RevokeAllSessionUrls() {
  let url_objects = GetSessionUrls();
  url_objects.forEach((obj) => {
    URL.revokeObjectURL(obj.url);
  });
  sessionStorage.setItem("object_urls", "[]");
}

export function TruncateText(text, max = MAX_CHARACTERS) {
  return Try(
    () => {
      if (text.length > max) {
        return text.substring(0, max) + "...";
      }
      return text;
    },
    (e) => {
      Log("error tuncating text: ", e);
      return "";
    }
  );
}

/**
 * This funciton can be awaited for x number of specified milliseconds in an async function
 * @param {number} ms  - the time in milliseconds to wait for
 * @param {func } callback  - (optional) -  the callback function to execute after the specified time
 * @returns {}  desc
 */
export function wait(ms, callback) {
  return new Promise((resolve) => {
    setTimeout(() => {
      if (callback) callback();
      return resolve(null);
    }, ms);
  });
}

export function uuid() {
  let values = [
    0,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
    "m",
    "n",
    "o",
    "p",
    "q",
    "r",
    "s",
    "t",
    "u",
    "v",
    "w",
    "x",
    "y",
    "z",
  ];
  let UUID = "";
  values.forEach((x) => {
    UUID += values[Math.floor(Math.random() * (values.length - 1))].toString();
  });
  return UUID;
}

export function Try(execute, onFail = (e) => {}) {
  try {
    return execute();
  } catch (e) {
    return onFail(e);
  }
}

/**
 * This function executes the specified callback every specified x time in milliseconds,
 * this funciton will run infinitely at the set interval
 * @param {number} ms  - the amount of time in milliseconds to wait before executing the callback
 * @param {func} callback  - (optional) -  the callback function to execute after the specified time
 * @returns {Promise<void>}  a null promise to allow the async await
 */
export function cron(ms, callback) {
  const maxTimeout = 2147483647; // Maximum timeout allowed in JavaScript
  (() => {
    return new Promise(async (resolve, reject) => {
      if (ms > maxTimeout) {
        let chunks = ms / maxTimeout;
        let full_iterations = Math.ceil(chunks);
        let remaining_time = full_iterations === 1 ? ms : ms % maxTimeout;
        for (let i = 0; i < full_iterations; i++) {
          await wait(maxTimeout);
        }
        await wait(remaining_time);
        if (callback) callback();
      } else {
        setTimeout(
          () => {
            if (callback) callback();
            resolve(null);
          },
          ms ? ms : 1
        );
      }
    });
  })().then(() => {
    cron(ms, callback);
  });
}

export function DisableAutoLoginTemp() {
  sessionStorage.setItem("disable-auto-login", true);
  wait(100, () => {
    sessionStorage.setItem("disable-auto-login", false);
  });
}

/**Inserts a specified character into a string every (freq) index of the string
 * retuns the formatted string
 * @param {string} string - the character to inser
 * @param {char} char - the character to insert
 * @param {char} freq - the frequecny to insert the char into the string
 * @returns {string}  the updated output
 */
export function InsertEvery(string, char, freq) {
  let output = "";
  let i = 0;
  let n = string.length - 1;
  string.split("").forEach((el) => {
    if (i != 0 && i != n && i % freq == 0) output += char + el;
    else output += el;
    i++;
  });
  return output;
}

/**
 * Executes some callback function [itr] times.
 * @param itr number of times to iterate
 * @param callback logic to execute once per iteration
 */
export function Iterations(itr, callback) {
  for (let i = 0; i < itr; i++) {
    callback();
  }
}

/**
 * Formats a phone number string from +15555555555 to +1 555-555-5555
 * @param {*} phone
 * @returns
 */
export function formatPhoneNumber(phone) {
  return Try(
    () => {
      //check for country code
      let chars = phone.toString().trim().split("");
      let prefix = "";
      if (chars[0] === "+") {
        prefix = chars[0] + chars[1];
        //remove prefix
        chars.splice(0, 2);
      }
      let _number = chars.join("");
      _number = InsertEvery(_number, "-", 3);
      chars = _number.split("");
      return prefix + " " + chars.join("");
    },
    () => {
      return phone;
    }
  );
}

/**
 * Gets all settings stored in local storage
 * @returns
 */
export function GetLocalSettings() {
  return tryParse(localStorage.getItem("settings")) ?? {};
}

/**
 * Returns the vlaue of the specified local setting,
 * returns null if setting does not exist
 * @param {*} key
 * @returns {string | null}
 */
export function GetLocalSetting(key) {
  return Try(
    () => {
      let existing_setting = GetLocalSettings();
      return existing_setting[key];
    },
    () => {
      return null;
    }
  );
}

/**
 * Sets the setting key value in local storage
 * @param {*} key
 * @param {*} value
 */
export function SetLocalSetting(key, value) {
  let existing_settings = GetLocalSettings();
  existing_settings[key] = value;
  localStorage.setItem("settings", JSON.stringify(existing_settings));
}

/**
 * Clears the local settings
 */
export function ClearLocalSettings() {
  localStorage.setItem("settings", "{}");
}

/**
 * Track object token in session storage
 * @param {*} objectId
 * @param {*} token
 */
export function TrackObjectToken(objectId, token) {
  let objectTokens = tryParse(sessionStorage.getItem("object-tokens"));
  if (!objectTokens) objectTokens = [];

  //find existing item if it exists
  let existing = objectTokens.find((x) => x.objectId === objectId);
  if (existing) {
    let targetIndex = objectTokens.indexOf(existing);
    objectTokens.splice(targetIndex, 1);
  }

  objectTokens.push({ objectId, token });
  sessionStorage.setItem("object-tokens", JSON.stringify(objectTokens));
}

/**
 * Get an object token from session storage
 * @param {*} objectId
 * @returns
 */
export function GetObjectToken(objectId) {
  try {
    let objectTokens = tryParse(sessionStorage.getItem("object-tokens"));
    if (!objectTokens) objectTokens = [];

    let item = objectTokens.find((x) => x.objectId === objectId);

    return item.token;
  } catch (error) {
    return null;
  }
}

/**
 * Returns the base dir or an empty string
 * @param {*} base_directory
 * @returns
 */
export function NormalizeBaseDirectory(base_directory) {
  if (base_directory.toLowerCase() === "trash") return "/" + base_directory;
  if (base_directory === "/") return "";
  else return base_directory;
}

/**
 * Generattes a Hash for the given input
 * @param {*} value
 * @returns
 */
export async function GetHash(value) {
  return "";
  // Convert input into a stable string representation
  let str;

  if (typeof value === "string" || typeof value === "number") {
    str = value.toString();
  } else if (typeof value === "object" && value !== null) {
    // Ensure consistent key ordering for objects
    str = JSON.stringify(value, Object.keys(value).sort());
  } else {
    str = String(value); // Handle booleans, null, undefined
  }

  // Convert string to Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(str);

  // Hash using SHA-256
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  // Convert hash to hex string
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Wrapper function to provide type ease for global alert
 * @param {()=> any} func context globalAlert function
 * @param {'info' | 'warning' | 'success' | 'error'} type
 * @param {string} header modal header text
 * @param {string} message modal details text
 * @param {()=> any} onAcknowledge callback function to fire onClose
 */
export function Alert(func, type, header, message, onAcknowledge, buttonText = "okay") {
  try {
    func(type, header, message, onAcknowledge, buttonText);
  } catch (error) {
    //do nothing
  }
}

/**
 * Logs To The Console When in Dev Mode
 * @param {*} prefix
 * @param {*} obj
 */
export function Log(prefix, obj) {
  if (ENV === "dev") console.log(prefix, obj);
}

//expose this function to the window
window.ClearLocalSettings = ClearLocalSettings;
